import type { ThemeContext } from './themeContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FlowThemeBurst, type FlowThemeBurstHandle } from './FlowThemeBurst';
import { FlowThemeCanvas } from './FlowThemeCanvas';
import type { FlowThemeId, FlowThemeProgress, SceneAnchor } from './engine/types';
import {
  trackFlowThemePerformanceFallback,
  trackFlowThemeRenderMode,
  type PerformanceFallbackReport,
} from './flowThemeAnalytics';
import type { FlowThemeDefinition } from './flowThemeRegistry';
import { maxPixelRatioForMode } from './renderBudget';
import { useFlowThemeRenderPolicy } from './useFlowThemeRenderPolicy';
import { useFlowThemeTransition, useFlowThemeTransitionBackdrop } from './FlowThemeTransition';
import { useOpaqueOnceEntered } from './useOpaqueOnceEntered';
import './flowThemes.css';

export interface FlowThemeBackdropProps {
  theme: FlowThemeDefinition;
  themeContext?: ThemeContext;
  progress: FlowThemeProgress;
  /**
   * Keep the backdrop mounted but invisible, with its scene paused (e.g. while
   * an embedded roleplay owns the page). Showing it again resumes the scene
   * where it was, without replaying the entrance.
   */
  hidden?: boolean;
  /** Reports whether the backdrop is showing its scene (or its static fallback). */
  onReadyChange?: (ready: boolean) => void;
  /**
   * Take over from a backdrop of the same theme that left the screen moments
   * ago (and hand over to the next one) without an entrance. For the learner
   * viewport only; previews that remount on purpose to replay leave it off.
   */
  handoff?: boolean;
  /**
   * Log the session's render mode and any performance fallback (learner
   * backdrops only, so admin previews don't count as learners).
   */
  trackRenderMode?: boolean;
  /** Overrides the default full-viewport placement (e.g. for contained previews). */
  className?: string;
}

const VIEWPORT_CLASS = 'fixed inset-0 -z-10';
/**
 * A backdrop of the same theme that left the screen this recently (a quiz
 * handing over to its results page) is taken over without the entrance fade
 * or the scene's intro, so the theme reads as continuous across the screens.
 */
const HANDOFF_WINDOW_MS = 1500;
let lastShown: { id: FlowThemeId; at: number } | null = null;

const clockNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Full-viewport animated background for a themed flow. It sits behind the
 * page content (inside the page's stacking context, above the route art) and
 * never takes pointer events. It fades in once the scene's first frame is
 * drawn, so the swatch never pops into the scene. Reduced motion, detected
 * software rendering and devices that can't keep up get one settled frame per
 * step and no bursts (see `useFlowThemeRenderPolicy`).
 */
export const FlowThemeBackdrop: React.FC<FlowThemeBackdropProps> = ({
  theme,
  progress,
  themeContext,
  hidden = false,
  onReadyChange,
  handoff = false,
  trackRenderMode = false,
  className,
}) => {
  const policy = useFlowThemeRenderPolicy(theme);
  const lowQuality = policy.lowQuality || themeContext?.preferences.quality === 'low';
  const [slowFallback, setSlowFallback] = useState(false);
  const motionAllowed = policy.mode !== 'still_reduced_motion'
    && !themeContext?.preferences.reducedMotion && themeContext?.preferences.animationIntensity !== 0;
  const isStill = policy.mode !== 'animated' || !motionAllowed;
  const burstRef = useRef<FlowThemeBurstHandle | null>(null);
  const [isHandoff] = useState(
    () => handoff
      && lastShown !== null
      && lastShown.id === theme.id
      && clockNow() - lastShown.at < HANDOFF_WINDOW_MS,
  );
  const [isReady, setIsReady] = useState(false);
  const isEntered = isReady || isHandoff;
  const backdropRef = useRef<HTMLDivElement>(null);
  const { isRetainingTheme } = useFlowThemeTransition();
  const isOpaque = useOpaqueOnceEntered(backdropRef, isEntered && !hidden);
  useFlowThemeTransitionBackdrop(backdropRef, {
    swatch: theme.swatch, ready: isReady, hidden: hidden || !handoff, opaque: isOpaque,
  });
  const onReadyChangeRef = useRef(onReadyChange);
  onReadyChangeRef.current = onReadyChange;
  const isShowingRef = useRef(false);
  isShowingRef.current = handoff && isEntered && !hidden;

  useEffect(() => {
    onReadyChangeRef.current?.(isReady);
  }, [isReady]);

  useEffect(() => {
    const themeId = theme.id;
    return () => {
      if (isShowingRef.current) {
        lastShown = { id: themeId, at: clockNow() };
      }
      onReadyChangeRef.current?.(false);
    };
  }, [theme.id]);

  const policyRef = useRef(policy);
  policyRef.current = policy;
  const trackRef = useRef(trackRenderMode);
  trackRef.current = trackRenderMode;

  useEffect(() => {
    const { mode, rendererClass, override, heldBySoftware } = policyRef.current;
    if (!trackRef.current) {
      return;
    }
    trackFlowThemeRenderMode({ theme: theme.id, mode, rendererClass, override });
    if (heldBySoftware) {
      trackFlowThemePerformanceFallback({
        theme: theme.id,
        reason: 'software_rendering',
        pixelRatio: null,
        p50Ms: null,
        p95Ms: null,
        elapsedSeconds: 0,
      }, override);
    }
  }, [theme.id]);

  const handlePerformanceFallback = useCallback((report: PerformanceFallbackReport) => {
    setSlowFallback(true);
    if (trackRef.current) {
      trackFlowThemePerformanceFallback(report, policyRef.current.override);
    }
  }, []);

  const handleReady = useCallback(() => setIsReady(true), []);
  const handleStep = useCallback((anchor: SceneAnchor | null) => {
    burstRef.current?.play('step', anchor);
  }, []);
  const handleFinale = useCallback((anchor: SceneAnchor | null) => {
    burstRef.current?.play('finale', anchor);
  }, []);
  const handleAnchor = useCallback((anchor: SceneAnchor | null) => {
    burstRef.current?.follow(anchor);
  }, []);

  return (
    <div
      ref={backdropRef}
      className={`flow-theme-backdrop pointer-events-none overflow-hidden ${className ?? VIEWPORT_CLASS}`}
      aria-hidden="true"
      data-testid="flow-theme-backdrop"
      data-flow-theme={theme.id}
      data-flow-theme-entered={isEntered ? 'true' : 'false'}
      data-flow-theme-hidden={hidden ? 'true' : undefined}
      data-flow-theme-handoff={isHandoff ? 'true' : undefined}
      data-flow-theme-transition={isRetainingTheme ? 'true' : undefined}
      data-flow-theme-render-mode={slowFallback ? 'still_slow' : policy.mode}
      style={{ background: theme.swatch }}
    >
      <FlowThemeCanvas
        key={lowQuality ? 'low' : 'standard'}
        theme={theme}
        progress={progress}
        themeContext={themeContext}
        still={isStill}
        transitionStillFrames={motionAllowed}
        paused={hidden}
        interactive
        skipIntro={isHandoff || isReady || isRetainingTheme}
        suppressInitialEvents={isReady}
        lowQuality={lowQuality}
        maxPixelRatio={maxPixelRatioForMode(policy.mode)}
        performanceMemory="session"
        allowStillFallback={policy.allowStillFallback}
        onStep={handleStep}
        onFinale={handleFinale}
        onAnchor={handleAnchor}
        onReady={handleReady}
        onPerformanceFallback={handlePerformanceFallback}
      />
      {isStill || slowFallback ? null : <FlowThemeBurst ref={burstRef} theme={theme} />}
    </div>
  );
};

export default FlowThemeBackdrop;
